import { IconMinus, IconPlus } from "@hawkins/assets";
import * as Accordion from "@radix-ui/react-accordion";
import { ResultOf } from "gql.tada";
import { AnimatePresence, motion } from "motion/react";
import { MouseEvent, useMemo, useRef, useState, useEffect } from "react";
import { FAQSectionById } from "../../queries/entries/FAQSectionById";
import { Button } from "../shared/core/Button";
import { FaqItem } from "../shared/FaqComponents/FaqItem";
import { FaqLayout } from "../shared/FaqComponents/FaqLayout";
import { FAQMobileFilters } from "../shared/FaqComponents/FaqMobileFilters";
import { BackgroundBlob } from "../shared/BackgroundBlob";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useLenis } from "lenis/react";
import { useSearchParams } from "react-router";

type FAQItem = NonNullable<
  NonNullable<
    ResultOf<typeof FAQSectionById>["faqSection"]
  >["faqItemsCollectionCollection"]
>["items"][number];
type FilterCategory = { id: string; label: string };

type FilterButtonProps = {
  category: FilterCategory;
  keyPrefix: string;
  isActive: boolean;
  onClick: (filterTag: string) => void;
};

const FilterButton = ({
  category,
  keyPrefix,
  isActive,
  onClick,
}: FilterButtonProps) => {
  return (
    <Button
      key={`${keyPrefix}-${category.id}`}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        onClick(category.id);
      }}
      variant={isActive ? "dark-on-light" : "light-on-blur"}
      size="small"
      aria-selected={isActive}
      className="rounded-full whitespace-nowrap hover:!scale-100 cursor-pointer"
      onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
      }}
      onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
      }}
    >
      {category.label}
      {isActive ? (
        <IconMinus size="small" className="w-4 h-4" data-no-roll-animate />
      ) : (
        <IconPlus size="small" className="w-4 h-4" data-no-roll-animate />
      )}
    </Button>
  );
};

export const FAQSection = ({
  entry: { faqSection: entry },
}: {
  entry: ResultOf<typeof FAQSectionById>;
}) => {
  const isDesktop = useIsDesktop();
  const lenis = useLenis();
  const [searchParams, setSearchParams] = useSearchParams();

  const questionsContainerRef = useRef<HTMLDivElement>(null);
  const faqs: (FAQItem | null)[] = useMemo(
    () => entry?.faqItemsCollectionCollection?.items || [],
    [entry]
  );
  const lastClickTimeRef = useRef(0);

  const filterCategories = useMemo<FilterCategory[]>(() => {
    const uniqueTags = new Set<string>();

    faqs.forEach((faq) => {
      if (faq && Array.isArray(faq.faqItemFilterTagText)) {
        faq.faqItemFilterTagText.forEach((tag: string | null) => {
          if (tag && tag.length) {
            uniqueTags.add(tag);
          }
        });
      }
    });

    return Array.from(uniqueTags).map((tag) => ({
      id: tag,
      label: tag,
    }));
  }, [faqs]);

  const activeFilters = useMemo(() => {
    const filters = searchParams.getAll("filter");
    return filters;
  }, [searchParams]);

  const [visibleFaqs, setVisibleFaqs] = useState<string[]>([]);

  const handleFilterClick = (filterTag: string) => {
    lastClickTimeRef.current = Date.now();

    const isActive = activeFilters.includes(filterTag);
    let newActiveFilters: string[];

    if (isActive) {
      newActiveFilters = activeFilters.filter((f) => f !== filterTag);
    } else {
      newActiveFilters = [...activeFilters, filterTag];
    }

    const newSearchParams = new URLSearchParams();
    newActiveFilters.forEach((filter) => {
      newSearchParams.append("filter", filter);
    });

    setSearchParams(newSearchParams);

    if (!isDesktop) {
      if (questionsContainerRef.current) {
        lenis?.scrollTo(questionsContainerRef.current as HTMLDivElement, {
          offset: -100,
        });
      }
    }
  };

  useEffect(() => {
    if (activeFilters.length === 0) {
      setVisibleFaqs(faqs.map((faq) => faq?.sys?.id || ""));
    } else {
      const itemsToShow = faqs
        .filter((faq) => {
          if (!faq || !Array.isArray(faq.faqItemFilterTagText)) return false;

          return activeFilters.every((tag: string | null) =>
            tag ? faq.faqItemFilterTagText?.includes(tag) : false
          );
        })
        .map((faq) => faq?.sys?.id || "");

      setVisibleFaqs(itemsToShow);
    }
  }, [activeFilters, faqs]);

  useEffect(() => {
    setVisibleFaqs(faqs.map((faq) => faq?.sys?.id || ""));
  }, [faqs]);

  return (
    <FaqLayout title="">
      <BackgroundBlob />
      <div className="w-full">
        <div className="flex flex-col dsk:grid dsk:grid-cols-12 gap-6 dsk:gap-12">
          <div className="dsk:col-span-12 md:hidden">
            <h2 className="text-heading-2 text-brand-cream text-left font-medium">
              {entry?.faqTitle || ""}
            </h2>
          </div>

          <div className="dsk:col-span-4 dsk:sticky dsk:top-48 dsk:self-start dsk:h-auto">
            <h2 className="text-heading-2 text-brand-cream text-left mb-8 font-medium hidden dsk:block">
              {entry?.faqTitle || ""}
            </h2>

            <p className="text-brand-cream mb-4 font-medium hidden dsk:block">
              {entry?.faqFilterTitle || ""}
            </p>
            <div className="hidden dsk:flex dsk:flex-wrap gap-2 max-w-full">
              {filterCategories.map((category) => (
                <FilterButton
                  key={`desktop-${category.id}`}
                  category={category}
                  keyPrefix="desktop"
                  isActive={activeFilters.includes(category.id)}
                  onClick={handleFilterClick}
                />
              ))}
            </div>
          </div>

          <div className="dsk:hidden w-full mb-6">
            <FAQMobileFilters
              filterTitle={entry?.faqFilterTitle || ""}
              filterCategories={filterCategories}
              activeFilters={activeFilters}
              onFilterClick={handleFilterClick}
              ref={questionsContainerRef}
            />
          </div>

          <div className="dsk:col-span-8 w-full" ref={questionsContainerRef}>
            <Accordion.Root type="multiple" className="w-full">
              <AnimatePresence mode="sync" initial={false}>
                {faqs.map((faq) => {
                  if (!faq) return null;

                  const isVisible = visibleFaqs.includes(faq?.sys?.id || "");

                  if (!isVisible && activeFilters.length > 0) {
                    return null;
                  }

                  return (
                    <motion.div
                      key={faq?.sys.id}
                      className="overflow-hidden"
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                    >
                      <FaqItem
                        id={faq?.sys.id}
                        title={faq?.faqItemTitle || ""}
                        description={faq?.faqItemDescriptionRichText}
                      />
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </Accordion.Root>
          </div>
        </div>
      </div>
    </FaqLayout>
  );
};
